import {
  keepPreviousData, useMutation, useQuery, useQueryClient
} from '@tanstack/react-query';

import APIGateway from '@/api';
import {
  DetailMetricsInfo,
  DetailRunInfo,
  EvalCreateReq,
  EvalRunsGetRes,
  EvalsGetRes,
  EvalsSearchParams,
  EvalsSearchRes,
  Evaluation,
  EvaluationDeprecateReq,
  EvaluationWithDetails,
  Example,
  ExampleMetricDataMap,
  MetricDataMap,
  RunCreateReq,
  RunWithDetails,
  Source
} from '@/types/evals';


export const evaluationsKeys = {
  all: [ 'evaluations' ] as const,
  search: ( params?: EvalsSearchParams ) => [ ...evaluationsKeys.all, 'search', params ] as const,
  runs: ( evalId: string, includeDetails?: boolean ) => [ ...evaluationsKeys.all, 'runs', evalId, { includeDetails } ] as const,
  runResults: ( evalId: string, runId: string ) => [ ...evaluationsKeys.all, 'runResults', evalId, runId ] as const,
  example: ( sourceId: string, version: number | 'latest', exampleId: string ) => [ ...evaluationsKeys.all, 'example', sourceId, version, exampleId ] as const
};

export function useEvaluations( options?: { refetchInterval?: number | false } ) {
  return useQuery<EvaluationWithDetails<DetailRunInfo>[]>( {
    queryKey: evaluationsKeys.all,
    queryFn: async() => {
      const evalsRes: EvalsGetRes = await APIGateway.evals.evalsGet();

      return evalsRes;
    },
    ...( options?.refetchInterval !== undefined ? { refetchInterval: options.refetchInterval } : {} )
  } );
}

export function useEvaluationsSearch( params: EvalsSearchParams ) {
  return useQuery<{ items: EvaluationWithDetails<DetailRunInfo>[]; total: number }>( {
    refetchOnWindowFocus: false,
    queryKey: evaluationsKeys.search( params ),
    queryFn: async() => {
      const res: EvalsSearchRes = await APIGateway.evals.evaluationsSearch( params );
      return { items: res.items, total: res.pagination.total };
    },
    placeholderData: keepPreviousData
  } );
}

export function useCreateEvaluation() {
  const queryClient = useQueryClient();

  return useMutation( {
    mutationFn: async( args: EvalCreateReq ) => {
      return await APIGateway.evals.evalCreate( args );
    },
    onSuccess: () => {
      // Invalidate and refetch evaluations after successful creation
      queryClient.invalidateQueries( { queryKey: evaluationsKeys.all } );
    }
  } );
}

export function useSetDeprecation( evalId: string ) {
  const queryClient = useQueryClient();

  return useMutation( {
    mutationFn: async( args: EvaluationDeprecateReq ) => {
      return await APIGateway.evals.evaluationDeprecate( evalId, args );
    },
    onSuccess: () => {
      // Invalidate & refetch evals after successful deprecation change
      queryClient.invalidateQueries( { queryKey: evaluationsKeys.all } );
    }
  } );
}

export function useEvalRuns( evalId: string, includeDetails: boolean = true ) {
  return useQuery( {
    queryKey: evaluationsKeys.runs( evalId, includeDetails ),
    queryFn: async( { queryKey } ) => {
      if ( !evalId ) throw new Error( 'Evaluation ID required' );

      // Extract includeDetails from queryKey to ensure consistency
      const [ , , , options ] = queryKey;
      const shouldIncludeDetails = ( options as { includeDetails?: boolean } )?.includeDetails ?? true;

      const result: EvalRunsGetRes = await APIGateway.evals.evalRunsGet( evalId, shouldIncludeDetails );

      if ( !result.evaluation || !result.runs ) {
        throw new Error( 'Unable to get evaluations or runs' );
      }

      return {
        evaluation: result.evaluation.evaluation,
        runs: result.runs.sort( ( a, b ) => b.run.createdAt.localeCompare( a.run.createdAt ) )
      };
    },
    enabled: !!evalId
  } );
}

export function useCreateRun( evalId: string ) {
  const queryClient = useQueryClient();

  return useMutation( {
    mutationFn: async( args: RunCreateReq ) => {
      if ( !evalId ) throw new Error( 'Evaluation ID required' );

      return await APIGateway.evals.runCreate( evalId, args );
    },
    onSuccess: () => {
      // Invalidate runs query to refetch after creating a new run
      // Use the same includeDetails value as the query (default is true)
      queryClient.invalidateQueries( { queryKey: evaluationsKeys.runs( evalId, true ) } );
    }
  } );
}

type EvaluationRunResultsData = {
  evaluation: Evaluation;
  run: RunWithDetails<DetailMetricsInfo>;
  metricDataMap: MetricDataMap;
  metricKeys: string[];
  exampleMetricDataMap: ExampleMetricDataMap;
  source?: Source;
  sourceName?: string;
  metricProviderNames: Record<string, string>;
  metricProviderVersions: Record<string, Record<string, number>>;
};

export function useExampleQuery( sourceId: string, version: number | 'latest', exampleId: string ) {
  return useQuery<Example | null>( {
    queryKey: evaluationsKeys.example( sourceId, version, exampleId ),
    queryFn: async() => {
      const examples = await APIGateway.evals.exampleGet( sourceId, version, exampleId );
      return examples[ 0 ] ?? null;
    },
    enabled: !!sourceId && !!exampleId
  } );
}

export function useEvaluationRunResults( evaluationId: string, runId: string ) {
  // eslint-disable-next-line @tanstack/query/exhaustive-deps -- runId is in the queryKey via evaluationsKeys.runResults; the rule can't see through the helper
  return useQuery<EvaluationRunResultsData>( {
    queryKey: evaluationsKeys.runResults( evaluationId, runId ),
    queryFn: async() => {
      if ( !evaluationId || !runId ) {
        throw new Error( 'Evaluation ID and Run ID are required' );
      }

      // Fetch main evaluation run results
      const evalRunsResultsRes = await APIGateway.evals.evalRunsResultsGet( evaluationId, runId, true );
      const evaluation = evalRunsResultsRes.evaluation.evaluation;
      const run = evalRunsResultsRes.runs[0];

      // Map metric data
      const metrics = run.details?.metrics || [];
      const metricDataMap = metrics.reduce<MetricDataMap>( ( acc, metric ) => {
        const key = `${ metric.metricProviderId }|${ metric.metricName }`;
        acc[ key ] = metric;
        return acc;
      }, {} );
      const metricKeys = Object.keys( metricDataMap ).sort();

      // Map example metric data

      const results = evalRunsResultsRes.results[ runId ] || [];
      const exampleMetricDataMap = results.reduce<ExampleMetricDataMap>( ( acc, result ) => {
        const { exampleId, metricProviderId, metricName } = result;
        if ( !acc[ exampleId ] ) acc[ exampleId ] = {};
        acc[ exampleId ][ `${ metricProviderId }|${ metricName }` ] = result;
        return acc;
      }, {} );

      // Fetch source data if applicable
      const shouldFetchSource = run.run.sourceId && run.run.sourceVersion;
      const sourceData = shouldFetchSource
        ? await APIGateway.evals.sourceGet( run.run.sourceId!, run.run.sourceVersion! ).catch( e => {
            console.error( 'Failed to fetch source:', e );
            return undefined;
          } )
        : undefined;

      // Fetch metric provider data
      const uniqueProviderIds = evaluation.metricProviders.map( mp => mp.metricProviderId );
      const providers = await Promise.all(
        uniqueProviderIds.map( id => APIGateway.evals.metricGet( id ).then( res => ( {
          id,
          name: res.name,
          versions: res.versions
        } ) ) )
      ).catch( e => {
        console.error( 'Failed to fetch metric providers:', e );
        return [];
      } );

      const metricProviderNames = providers.reduce<Record<string, string>>( ( acc, p ) => {
        acc[ p.id ] = p.name;
        return acc;
      }, {} );

      const metricProviderVersions = providers.reduce<Record<string, Record<string, number>>>( ( acc, p ) => {
        acc[ p.id ] = p.versions.reduce<Record<string, number>>( ( vAcc, v ) => {
          vAcc[ v.id ] = v.version;
          return vAcc;
        }, {} );
        return acc;
      }, {} );

      return {
        evaluation,
        run,
        metricDataMap,
        metricKeys,
        exampleMetricDataMap,
        source: sourceData,
        sourceName: sourceData?.name,
        metricProviderNames,
        metricProviderVersions
      };
    },
    enabled: !!evaluationId && !!runId
  } );
}
